import React, { useState, useEffect, useMemo } from 'react';
import { 
    query, 
    collection, 
    where, 
    onSnapshot, 
    doc, 
    deleteDoc, 
    addDoc, 
    updateDoc, 
    serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

import PlannerIcons from '../../components/shared/PlannerIcons';
import LocationSearchInput from '../../components/ui/LocationSearchInput';
import ImageUpload from '../../components/ui/ImageUpload';
import { compressImage } from '../../utils/helpers';
import { GEMINI_API_KEY } from '../../config/firebase';

function PlannerServiceForm({ user, db, storage, onComplete, initialData, isMapsScriptLoaded }) {
    const [title, setTitle] = useState(initialData?.title || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [price, setPrice] = useState(initialData?.price || '');
    const [location, setLocation] = useState(initialData?.location || '');
    const [serviceType, setServiceType] = useState(initialData?.serviceType || 'Day-Of Coordination');
    const [idealFor, setIdealFor] = useState(initialData?.idealFor?.join(', ') || '');
    const [existingImageUrls, setExistingImageUrls] = useState(initialData?.imageUrls || []);
    const [includedItems, setIncludedItems] = useState(initialData?.includedItems || []);
    const [currentItem, setCurrentItem] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);

    const handleRemoveExistingImage = (index) => {
        setExistingImageUrls(prev => prev.filter((_, i) => i !== index));
    };

    const handleAddItem = () => {
        if (currentItem.trim() !== '') {
            setIncludedItems([...includedItems, currentItem.trim()]);
            setCurrentItem('');
        }
    };

    const handleRemoveItem = (indexToRemove) => {
        setIncludedItems(includedItems.filter((_, index) => index !== indexToRemove));
    };

    const handleGenerateDescription = async () => {
        if (!title) {
            setError("Please enter a package title to generate a description.");
            return;
        }
        setAiLoading(true);
        try {
            const prompt = `Write a compelling and professional service description for an event planner offering a package named "${title}". Highlight the benefits, peace of mind, and professional expertise provided. The description should be about 3-4 sentences long.`;
            const payload = { contents: [{ role: "user", parts: [{ text: prompt }] }] };
            
            const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${GEMINI_API_KEY}`, { 
                method: 'POST', 
                headers: { 'Content-Type': 'application/json' }, 
                body: JSON.stringify(payload) 
            });
            
            const result = await response.json();
            if (result.candidates?.[0]) {
                setDescription(result.candidates[0].content.parts[0].text);
            }
        } catch (err) {
            setError("Could not generate description.");
        } finally {
            setAiLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (existingImageUrls.length === 0) {
            setError("Please upload at least one image to showcase your services.");
            setLoading(false);
            return;
        }

        try {
            const listingData = {
                title, 
                description, 
                price: parseFloat(price), 
                location, 
                category: 'planners',
                serviceType,
                idealFor: idealFor.split(',').map(tag => tag.trim()).filter(Boolean),
                tags: ['planning package', 'coordinator', 'professional service', serviceType.toLowerCase()], 
                includedItems, 
                inventory: 999,
                imageUrls: existingImageUrls,
                vendorId: user.uid,
                isService: true,
                updatedAt: serverTimestamp()
            };

            if (initialData) {
                await updateDoc(doc(db, "listings", initialData.id), listingData);
            } else {
                await addDoc(collection(db, "listings"), { ...listingData, createdAt: serverTimestamp() });
            }
            onComplete();
        } catch (err) {
            setError("Failed to save service package.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-2xl shadow-lg mt-4 border border-pink-100 animate-fade-in">
            <div className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-5">
                <div className="w-12 h-12 bg-pink-50 text-pink-600 flex items-center justify-center rounded-2xl text-2xl shadow-inner">📋</div>
                <div>
                    <h3 className="text-xl font-bold text-gray-900">{initialData ? 'Edit Service Package' : 'Create Service Package'}</h3>
                    <p className="text-sm text-gray-500 font-medium">Define your offerings for the Host-it! marketplace.</p>
                </div>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 ml-1">Package Title</label>
                        <input type="text" placeholder="e.g. Month-Of Wedding Coordination" value={title} onChange={e => setTitle(e.target.value)} className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-pink-500 outline-none transition font-bold" required />
                    </div>
                    
                    <div className="md:col-span-2">
                        <div className="flex justify-between items-center mb-1.5">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Package Description</label>
                            <button type="button" onClick={handleGenerateDescription} disabled={aiLoading} className="text-[10px] font-black uppercase tracking-widest bg-purple-50 text-purple-600 px-3 py-1 rounded-full border border-purple-100 hover:bg-purple-100 transition disabled:opacity-50">✨ AI Generate</button>
                        </div>
                        <textarea placeholder="Describe what clients can expect..." value={description} onChange={e => setDescription(e.target.value)} className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-pink-500 outline-none transition resize-none h-32 font-medium leading-relaxed" required></textarea>
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 ml-1">Base Price ($)</label>
                        <input type="number" placeholder="e.g. 1500" value={price} onChange={e => setPrice(e.target.value)} className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-pink-500 outline-none transition font-bold" required />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1.5 ml-1">Service Area (City)</label>
                        <LocationSearchInput 
                            initialValue={location} 
                            onPlaceSelected={setLocation} 
                            isFormInput={true}
                            isMapsScriptLoaded={isMapsScriptLoaded}
                            placeholder="e.g. Los Angeles, CA"
                            className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-pink-500 outline-none transition font-bold"
                        />
                    </div>
                </div>

                <div className="border-t border-gray-100 pt-6">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 ml-1">Portfolio & Images</label>
                    <div className="flex flex-wrap gap-4 items-center bg-gray-50 p-4 rounded-2xl border border-gray-200 border-dashed">
                        {existingImageUrls.map((url, index) => (
                            <div key={url} className="relative group w-24 h-24 rounded-xl overflow-hidden shadow-sm border border-gray-200">
                                <img src={url} className="w-full h-full object-cover" alt="Service" />
                                <button type="button" onClick={() => handleRemoveExistingImage(index)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition shadow-lg">&times;</button>
                            </div>
                        ))}
                        <div className="w-full">
                            <ImageUpload 
                                user={user}
                                storage={storage}
                                onUploadComplete={(url) => setExistingImageUrls(prev => [...prev, url])}
                                buttonText="+ Add Portfolio Image"
                                hideLabel={true}
                            />
                        </div>
                    </div>
                </div>
                
                <div className="border-t border-gray-100 pt-6">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 ml-1">Included Deliverables</label>
                    <div className="flex gap-2 mb-4">
                        <input type="text" value={currentItem} onChange={e => setCurrentItem(e.target.value)} placeholder="e.g. 10 hours of day-of coordination" className="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white outline-none transition text-sm font-medium" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddItem())} />
                        <button type="button" onClick={handleAddItem} className="bg-gray-900 text-white px-6 rounded-xl text-sm font-black hover:bg-black transition shadow-md transform active:scale-95">Add</button>
                    </div>
                    <div className="space-y-2">
                        {includedItems.map((item, index) => (
                            <div key={index} className="flex justify-between items-center bg-white px-4 py-3 rounded-xl border border-gray-100 text-sm font-bold text-gray-700 shadow-sm animate-fade-in">
                                <div className="flex items-center gap-3"><span className="text-emerald-500">✓</span> {item}</div>
                                <button type="button" onClick={() => handleRemoveItem(index)} className="text-gray-300 hover:text-red-500 transition-colors">&times;</button>
                            </div>
                        ))}
                    </div>
                </div>

                {error && <p className="text-red-500 text-sm font-bold bg-red-50 p-3 rounded-lg border border-red-100">{error}</p>}

                <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                    <button type="button" onClick={onComplete} className="bg-white border border-gray-300 text-gray-700 px-8 py-3 rounded-xl font-bold hover:bg-gray-50 transition">Cancel</button>
                    <button type="submit" disabled={loading} className="bg-gray-900 text-white px-10 py-3 rounded-xl font-black text-sm hover:bg-black transition shadow-lg transform active:scale-95 disabled:opacity-50">
                        {loading ? 'Publishing...' : 'Save & Publish Service'}
                    </button>
                </div>
            </form>
        </div>
    );
}

/**
 * Main Component: Planner Services Tab
 */
export default function PlannerServicesTab({ user, db, storage, isMapsScriptLoaded }) {
    const [listings, setListings] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingListingId, setEditingListingId] = useState(null);
    const [listingToDelete, setListingToDelete] = useState(null);

    useEffect(() => {
        if (!user || !db) return;
        const qListings = query(
            collection(db, "listings"), 
            where("vendorId", "==", user.uid), 
            where("category", "==", "planners")
        );
        const unsubscribe = onSnapshot(qListings, (snapshot) => {
            setListings(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (err) => console.error("Firestore listener error:", err));
        return () => unsubscribe();
    }, [user, db]);

    const handleDeleteListing = async () => {
         if (!listingToDelete) return;
         try {
             await deleteDoc(doc(db, "listings", listingToDelete.id));
             setListingToDelete(null);
         } catch (err) {
             console.error("Delete failed", err);
         }
    };

    if (showAddForm || editingListingId) {
        return (
            <div className="p-4 md:p-8 animate-fade-in max-w-4xl mx-auto w-full">
                <button 
                    onClick={() => { setShowAddForm(false); setEditingListingId(null); }} 
                    className="mb-6 bg-white border border-gray-200 text-gray-600 px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 hover:bg-gray-50 transition shadow-sm"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    Return to Portfolio
                </button>
                <PlannerServiceForm
                    user={user}
                    db={db}
                    storage={storage}
                    onComplete={() => { setShowAddForm(false); setEditingListingId(null); }}
                    initialData={editingListingId ? listings.find(l => l.id === editingListingId) : null}
                    isMapsScriptLoaded={isMapsScriptLoaded}
                />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-10 animate-fade-in h-full overflow-y-auto custom-scrollbar">
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-10 gap-6">
                    <div>
                        <h1 className="text-3xl font-serif font-bold text-gray-900 leading-tight">Professional Services</h1>
                        <p className="text-sm md:text-base text-gray-500 font-medium mt-1">Curate the planning packages your clients can book directly from your profile.</p>
                    </div>
                    <button 
                        onClick={() => setShowAddForm(true)} 
                        className="bg-pink-600 hover:bg-pink-700 text-white px-8 py-3.5 rounded-2xl font-black text-sm transition shadow-lg shadow-pink-500/20 transform active:scale-95 flex items-center justify-center gap-2"
                    >
                        <PlannerIcons.Plus /> Create New Package
                    </button>
                </div>

                {}
                {listingToDelete && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-fade-in">
                        <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-sm w-full border border-gray-100">
                            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center text-3xl mb-6 mx-auto">🗑️</div>
                            <h3 className="text-2xl font-bold mb-2 text-center text-gray-900">Delete Service?</h3>
                            <p className="mb-8 text-gray-500 text-sm text-center leading-relaxed">This will remove the package from your profile. Active bookings will not be affected.</p>
                            <div className="flex gap-3">
                                <button onClick={() => setListingToDelete(null)} className="flex-1 px-4 py-3 bg-gray-100 text-gray-600 rounded-xl font-bold hover:bg-gray-200 transition">Cancel</button>
                                <button onClick={handleDeleteListing} className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition shadow-md">Confirm</button>
                            </div>
                        </div>
                    </div>
                )}

                {}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {listings.length > 0 ? listings.map(listing => (
                        <div key={listing.id} className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden flex flex-col hover:shadow-2xl hover:border-pink-200 transition-all duration-500 group relative">
                            <div className="h-56 relative overflow-hidden bg-gray-100">
                                <img src={listing.imageUrls?.[0] || 'https://placehold.co/600x400/fdf2f8/db2777?text=Service'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt={listing.title} />
                                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-xl text-sm font-black shadow-lg text-pink-600 border border-pink-100">
                                    ${parseFloat(listing.price).toLocaleString()}
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </div>
                            <div className="p-6 flex-1 flex flex-col">
                                <h3 className="font-bold text-xl text-gray-900 mb-2 truncate group-hover:text-pink-600 transition-colors">{listing.title}</h3>
                                <p className="text-sm text-gray-500 font-medium line-clamp-3 mb-6 leading-relaxed flex-1">{listing.description}</p>
                                <div className="flex justify-between gap-3 border-t border-gray-100 pt-5">
                                    <button onClick={() => setEditingListingId(listing.id)} className="flex-1 bg-gray-900 text-white py-3 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black transition shadow-md transform active:scale-95">
                                        Edit Details
                                    </button>
                                    <button onClick={() => setListingToDelete(listing)} className="px-4 bg-gray-50 text-gray-400 hover:text-red-500 border border-gray-100 rounded-xl transition shadow-sm">
                                        <PlannerIcons.Trash />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )) : (
                        <div className="col-span-full py-24 text-center border-[3px] border-dashed border-gray-200 rounded-[3rem] bg-white px-6">
                            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner text-5xl grayscale opacity-50">💼</div>
                            <h3 className="text-2xl font-serif font-bold text-gray-900">Your storefront is empty</h3>
                            <p className="mt-2 mb-8 max-w-sm mx-auto text-gray-500 font-medium leading-relaxed">Add planning packages (like Day-of Coordination or Full Service) to get discovered by clients on the marketplace.</p>
                            <button onClick={() => setShowAddForm(true)} className="bg-gray-900 text-white px-10 py-4 rounded-2xl font-black text-sm hover:bg-black transition shadow-xl transform active:scale-95 flex items-center justify-center gap-2 mx-auto">
                                <PlannerIcons.Plus /> Create Your First Package
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}